import React, { useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link2, Twitter, Instagram, Download, Loader2, Check } from 'lucide-react';
import { WallPost } from '@/hooks/useSocialWall';
import { useSharePost } from '@/hooks/useSharePost';
import { cn } from '@/lib/utils';

interface SharePostModalProps {
  post: WallPost;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SharePostModal: React.FC<SharePostModalProps> = ({
  post,
  open,
  onOpenChange,
}) => {
  const { copyLink, shareToTwitter, shareToInstagram, incrementShareCount } = useSharePost();
  const [downloading, setDownloading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCopyLink = async () => {
    const success = await copyLink(post.id);
    if (success) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleShareTwitter = () => {
    shareToTwitter(post.id, post.content || '', post.profiles?.name || 'Athlete');
    onOpenChange(false);
  };

  const handleShareInstagram = async () => {
    await shareToInstagram(post.id, post.content || '');
  };

  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#002E62',
      });
      
      const link = document.createElement('a');
      link.download = `recruitlook-post-${post.id.slice(0, 8)}.png`;
      link.href = dataUrl;
      link.click();
      
      await incrementShareCount(post.id);
    } catch (error) {
      console.error('Failed to generate image:', error);
    } finally {
      setDownloading(false);
    }
  };

  const truncatedContent = post.content && post.content.length > 280 
    ? post.content.substring(0, 280) + '...' 
    : post.content;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Post</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Share Options */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4"
              onClick={handleCopyLink}
            >
              {linkCopied ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <Link2 className="h-5 w-5" />
              )}
              <span className="text-sm">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
            </Button>

            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4"
              onClick={handleShareTwitter}
            >
              <Twitter className="h-5 w-5" />
              <span className="text-sm">Twitter/X</span>
            </Button>

            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4"
              onClick={handleShareInstagram}
            >
              <Instagram className="h-5 w-5" />
              <span className="text-sm">Instagram</span>
            </Button>

            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4"
              onClick={handleDownloadImage}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
              <span className="text-sm">Download Image</span>
            </Button>
          </div>

          {/* Image Preview Card - Hidden until download */}
          <div className="overflow-hidden rounded-lg">
            <div
              ref={cardRef}
              className="p-6"
              style={{
                background: 'linear-gradient(135deg, #002E62 0%, #001a3a 100%)',
                color: 'white',
                fontFamily: 'system-ui, sans-serif',
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="h-12 w-12 border-2 border-white/20">
                  <AvatarImage
                    src={post.profiles?.avatar_url || undefined}
                    alt={post.profiles?.name || 'User'}
                  />
                  <AvatarFallback className="bg-[#BC8D09] text-white font-bold">
                    {getInitials(post.profiles?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-bold text-lg">{post.profiles?.name || 'Athlete'}</p>
                </div>
              </div>

              {/* Content */}
              {truncatedContent && (
                <p className="text-base leading-relaxed mb-4 whitespace-pre-wrap">
                  {truncatedContent}
                </p>
              )}

              {/* Media thumbnail */}
              {post.media_urls && post.media_urls.length > 0 && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  <img
                    src={post.media_urls[0]}
                    alt="Post media"
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}

              {/* Footer Branding */}
              <div className="flex items-center justify-between pt-4 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 bg-[#BC8D09] rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold">RL</span>
                  </div>
                  <span className="font-bold text-sm">RecruitLook</span>
                </div>
                <span className="text-xs text-white/60">recruitlook.com</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Download as image to share on Instagram Stories
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePostModal;
